import { Box, Stack, styled, Tooltip, Typography } from '@mui/joy';
import { ArrowRight } from '@phosphor-icons/react';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextEllipsis, TextLine } from 'core/elements';
import { getAgGridTableCellData } from 'core/utils';
import { QuotePartColId } from 'quotes/components/QuotePartList';
import { TaxonomyHelper } from 'taxonomy/helpers';
import { Color, Level, TooltipPosition } from 'theme/enums';

const Wrapper = styled(Box)({
  display: 'flex',
  gap: '4px',
  flexWrap: 'wrap',
  alignItems: 'center',
});

export const TaxonomyCell = (params: ICellRendererParams) => {
  const { t } = useTranslation();

  const data = getAgGridTableCellData(params, QuotePartColId.TAXONOMY);
  const isGroupedByColumn = QuotePartColId.TAXONOMY === params.node?.rowGroupColumn?.getColId();

  if (!data || isGroupedByColumn) {
    return <TextLine text={params.value} />;
  }

  const { taxonomyPath } = data;

  if (!taxonomyPath || !taxonomyPath.length) {
    return DEFAULT_PLACEHOLDER;
  }

  const taxonomy = TaxonomyHelper.getLastLevelTaxonomy(taxonomyPath);
  const taxonomyPathNames = TaxonomyHelper.getTaxonomyPathNames(taxonomyPath);

  if (taxonomy.level === 1 || taxonomy.level === 2) {
    return <TaxonomyNames names={taxonomyPathNames} />;
  }

  return (
    <Stack>
      <TaxonomyNames names={[taxonomyPathNames[0], '...', taxonomy.name]} />

      <Tooltip
        title={<TaxonomyNames names={taxonomyPathNames} />}
        placement={TooltipPosition.BOTTOM_START}
      >
        <Typography level={Level.BODY_SM} color={Color.PRIMARY}>
          {t('labelMoreCategories', { count: taxonomyPathNames.length - 2 })}
        </Typography>
      </Tooltip>
    </Stack>
  );
};

const TaxonomyNames = ({ names }: { names: string[] }) => {
  return (
    <Wrapper>
      {names.map((name, index) => (
        <Wrapper key={name}>
          <TextEllipsis content={name} />
          {index < names.length - 1 && <ArrowRight style={{ fontSize: '15px' }} />}
        </Wrapper>
      ))}
    </Wrapper>
  );
};
